// src/web-app/documents/dashboard.js
// Documents dashboard view (plan Phase 2A): the web app's landing view.
//
// Two zones:
//  - ACTIVE (top, with the dropzone): documents added this session and jobs
//    currently running — rendered as cards with live stage progress.
//  - HISTORY (below): every other registry document, as compact rows showing
//    verification state (verified / partly verified / not verified) and
//    available reports.
//
// Deliberately does NOT import queue-manager.

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// One report row inside the "Reports (n)" panel: the download link plus its
// sharing controls. Reports are private by default; sharing mints a revocable
// (optionally password-protected) public link resolved server-side at /r/{token}.
function reportRowHtml(r, { canManageShare = true } = {}) {
  const rid = escapeHtml(r.report_id || '');
  const type = escapeHtml((r.report_type || 'html').toUpperCase());
  const when = r.created_at ? escapeHtml(formatDate(r.created_at)) : '';
  const share = !canManageShare
    ? '<span class="dashboard-share-state">Older version</span>'
    : r.shared
    ? `<span class="dashboard-share-state" title="${r.share_has_password ? 'Password protected — recipients need the password' : 'Anyone with the link can view'}">${r.share_has_password ? '🔒 ' : ''}Shared</span>
       <button class="btn-text btn-small dashboard-share-copy" type="button" data-report-id="${rid}" data-share-url="${escapeHtml(r.share_url || '')}" title="Copy the share link">Copy link</button>
       <button class="btn-text btn-small dashboard-share-revoke" type="button" data-report-id="${rid}" title="Stop sharing this report">Unshare</button>`
    : `<button class="btn-text btn-small dashboard-share" type="button" data-report-id="${rid}" title="Create a shareable link">Share…</button>`;
  return `
    <div class="dashboard-report-row">
      <button type="button" class="dashboard-report-link" data-report-id="${rid}" title="Open ${escapeHtml(r.report_name || 'Report')}">${escapeHtml(r.report_name || 'Report')}</button>
      <span class="dashboard-report-meta small muted"><span class="dashboard-report-type">${type}</span>${when ? ` ${when}` : ''}</span>
      <span class="dashboard-report-share">${share}</span>
    </div>`;
}

async function copyToClipboard(text) {
  if (!text) return false;
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch { /* fall through to the legacy browser copy path */ }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.cssText = 'position:fixed;left:-9999px;top:0;opacity:0;';
  document.body.appendChild(textarea);
  textarea.select();
  let copied = false;
  try { copied = document.execCommand('copy'); } catch { /* unsupported */ }
  textarea.remove();
  return copied;
}

// Clipboard writes commonly require the original click's user activation.
// Sharing needs an API round-trip before the URL exists, so start a deferred
// ClipboardItem write synchronously and resolve its contents with that URL.
function beginDeferredClipboardCopy() {
  if (!navigator.clipboard?.write || typeof ClipboardItem === 'undefined') return null;
  let resolveText;
  let rejectText;
  const text = new Promise((resolve, reject) => {
    resolveText = resolve;
    rejectText = reject;
  });
  let write;
  try {
    const item = new ClipboardItem({
      'text/plain': text.then((value) => new Blob([value], { type: 'text/plain' })),
    });
    write = navigator.clipboard.write([item]).then(() => true).catch(() => false);
  } catch {
    return null;
  }
  return {
    async complete(value) {
      resolveText(value);
      return write;
    },
    cancel() {
      rejectText(new Error('Share creation failed'));
    },
  };
}

// Stage-granular pill text from the §1.5 progress payload. The server resolves
// every unique work before it analyses anything, so the stages are serial and we
// show only the one that is running. The two counters have DIFFERENT
// denominators: resolve counts unique works, analyse counts checkable citations.
// Neither counts out of citations_total, which also includes the rows the type
// filter dropped (in-case material, ibid, commentary) — reported separately, and
// worth surfacing, since a big gap is otherwise mistaken for missing work.
// works_total/checkable_total are absent on jobs that started before this
// payload existed; fall back to citations_total so old cards still read sanely.
function progressText(job) {
  const status = job?.status || 'queued';
  if (status === 'queued') return 'Queued…';
  const p = job?.progress;
  if (!p || p.stage === 'parsing' || p.citations_total == null) return 'Parsing…';

  const works = p.works_total ?? p.citations_total;
  const checkable = p.checkable_total ?? p.citations_total;
  const resolved = p.resolved || 0;
  const analysed = p.analysed || 0;

  const skipped = p.skipped_total || 0;
  const note = skipped > 0 ? ` · ${skipped} of ${p.citations_total} not checkable` : '';

  if (resolved < works) return `Resolving ${resolved}/${works} sources${note}`;
  return `Analysing ${analysed}/${checkable}${note}`;
}

function cardState(entry) {
  if (entry.upload?.status === 'ready') return 'ready';
  if (entry.upload?.status === 'queued_local' || entry.upload?.status === 'uploading') return 'uploading';
  if (entry.upload?.status === 'submit_failed') return 'failed';
  const jobStatus = entry.job?.status;
  if (jobStatus === 'failed' || jobStatus === 'error') return 'failed';
  if (jobStatus === 'cancelled') return 'cancelled';
  if (jobStatus && jobStatus !== 'completed') return 'processing';
  if (entry.verifySummary) return 'verified';
  if ((entry.partialChecks || 0) > 0) return 'partial';
  return 'idle';
}

// Current coarse stage for pill colouring (mirrors the Desktop queue pill:
// parsing amber, resolving blue, analysing indigo).
function jobStage(job) {
  if ((job?.status || 'queued') === 'queued') return 'queued';
  const p = job?.progress;
  if (!p || p.stage === 'parsing' || p.citations_total == null) return 'parsing';
  if ((p.resolved || 0) < (p.works_total ?? p.citations_total)) return 'resolving';
  return 'analysing';
}

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

// File names are shown without the extension; the full name stays in the tooltip.
const displayName = (name) => String(name || 'Untitled').replace(/\.pdf$/i, '');

const summaryIssues = (summary) => summary?.issues ?? 0;

function statusPill(entry, state) {
  switch (state) {
    case 'ready': return { text: 'Ready to verify', cls: 'idle' };
    case 'uploading': return { text: 'Uploading…', cls: 'processing is-running' };
    case 'processing': return entry.job?.cancelRequested
      ? { text: 'Stopping…', cls: 'stage-queued is-running' }
      : { text: progressText(entry.job), cls: `stage-${jobStage(entry.job)} is-running` };
    case 'cancelled': return { text: 'Cancelled', cls: 'idle' };
    case 'failed': return { text: 'Failed', cls: 'failed' };
    case 'verified': return summaryIssues(entry.verifySummary) > 0
      ? { text: 'Issues found', cls: 'issues', title: 'Verified. Some citations may not support the text; review the flagged ones.' }
      : { text: 'Verified', cls: 'verified' };
    case 'partial': return {
      text: 'Partly verified',
      cls: 'partial',
      title: `${plural(entry.partialChecks, 'citation')} checked so far. Open the document to finish, or verify it again.`,
    };
    default: return { text: 'Not verified', cls: 'idle' };
  }
}

function pillHtml(pill, suffix = '') {
  return `<span class="dashboard-status-pill ${pill.cls}"${pill.title ? ` title="${escapeHtml(pill.title)}"` : ''}>${escapeHtml(pill.text)}${suffix}</span>`;
}

// Labelled counts (the citation tabs in the document view use the same words).
// Zero counts are muted so the issue count is what stands out.
function countsHtml(entry) {
  const summary = entry.verifySummary;
  if (!summary) {
    return entry.partialChecks > 0
      ? `<span class="dashboard-counts"><span class="dashboard-count is-zero">${plural(entry.partialChecks, 'check')}</span></span>`
      : '';
  }
  const verified = summary.citations_verified ?? 0;
  const issues = summaryIssues(summary);
  const unresolved = summary.unresolved ?? Math.max(0, (summary.citations_unverified ?? 0) - issues);
  const count = (n, cls, label, title) =>
    `<span class="dashboard-count ${cls}${n === 0 ? ' is-zero' : ''}" title="${title}">${escapeHtml(label)}</span>`;
  return `
    <span class="dashboard-counts">
      ${count(verified, 'verified', `${verified} verified`, 'Citations whose source supports the text')}
      ${count(issues, 'issues', plural(issues, 'issue'), 'Citations whose source may not support the text')}
      ${count(unresolved, 'unresolved', `${unresolved} unresolved`, 'Citations whose source could not be found')}
    </span>`;
}

// A freshly selected file can be opened from its in-memory File object. Saved
// documents remain openable once the server has enough data to restore them.
const canOpenEntry = (entry, state) =>
  !!entry.file
  || (!!entry.documentId && (!!entry.hasOcrCache || !!entry.inRegistry || state === 'verified'));

const isBusy = (state) => state === 'uploading' || state === 'processing';

// The one visible action, chosen by state: start the work if it has not run,
// show the result if it has, otherwise open the document.
function primaryAction(entry, state) {
  if (isBusy(state)) return null;
  if (state === 'failed' && entry.file) return { action: 'verify', label: 'Retry', cls: 'btn-primary' };
  if (entry.file && state !== 'verified' && state !== 'failed') return { action: 'verify', label: 'Verify', cls: 'btn-primary' };
  if ((entry.reportCount || 0) > 0 && entry.documentId) return { action: 'view-report', label: 'View report', cls: 'btn-outline' };
  if (canOpenEntry(entry, state)) return { action: 'open', label: 'Open', cls: 'btn-outline' };
  return null;
}

const MENU_ICON = '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="1.8"/><circle cx="12" cy="12" r="1.8"/><circle cx="19" cy="12" r="1.8"/></svg>';

function actionsHtml(entry, state, { withOpen = false } = {}) {
  const primary = primaryAction(entry, state);
  const showOpen = withOpen && primary?.action !== 'open' && canOpenEntry(entry, state) && !isBusy(state);
  return `
    ${primary ? `<button class="${primary.cls} btn-small" type="button" data-action="${primary.action}">${primary.label}</button>` : ''}
    ${showOpen ? '<button class="btn-text btn-small" type="button" data-action="open">Open</button>' : ''}
    <button class="btn-icon dashboard-menu-btn" type="button" data-action="menu" title="More actions" aria-label="More actions" aria-haspopup="menu">${MENU_ICON}</button>`;
}

function cardHtml(entry) {
  const state = cardState(entry);
  const pill = statusPill(entry, state);
  const error = entry.upload?.error || entry.job?.error;
  const busy = isBusy(state);
  const emailSuffix = entry.job?.workflow === 'email_report' && busy ? ' · will email' : '';
  const clickable = canOpenEntry(entry, state) && !busy;

  return `
  <div class="dashboard-card state-${state}${clickable ? ' is-clickable' : ''}" data-doc-id="${escapeHtml(entry.key)}"${clickable ? ' tabindex="0"' : ''}>
    <div class="dashboard-card-header">
      <span class="dashboard-card-name" title="${escapeHtml(entry.name)}">${escapeHtml(displayName(entry.name))}</span>
    </div>
    <div class="dashboard-card-meta">
      ${pillHtml(pill, emailSuffix)}
      ${entry.project ? `<span class="dashboard-project-chip" title="Project: ${escapeHtml(entry.project)}">${escapeHtml(entry.project)}</span>` : ''}
      ${entry.updatedAt ? `<span class="dashboard-card-date">${escapeHtml(formatDate(entry.updatedAt))}</span>` : ''}
    </div>
    ${countsHtml(entry)}
    ${error ? `<div class="dashboard-card-error" title="${escapeHtml(error)}">${escapeHtml(error)}</div>` : ''}
    <div class="dashboard-card-actions">${actionsHtml(entry, state, { withOpen: true })}</div>
    <div class="dashboard-reports-panel" hidden></div>
    ${busy ? `
    <div class="dashboard-card-overlay">
      <div class="dashboard-overlay-content">
        <div class="loading-spinner-small"></div>
        ${pillHtml(pill, emailSuffix)}
        ${entry.job && !entry.job.cancelRequested
          ? '<button class="btn-secondary btn-small" type="button" data-action="stop" title="Stop this verification run">Stop</button>'
          : ''}
      </div>
    </div>` : ''}
  </div>`;
}

function historyRowHtml(entry) {
  const state = cardState(entry);
  const pill = statusPill(entry, state);
  const clickable = canOpenEntry(entry, state);

  // A job that failed after the verification stamp (e.g. report rendering)
  // leaves the doc "Verified" but incomplete — surface it.
  const warn = entry.lastJobError
    ? `<span class="dashboard-history-warn" title="${escapeHtml(`Last run failed: ${entry.lastJobError}`)}">⚠</span>`
    : '';

  // Every cell renders unconditionally (empty when n/a) so the subgrid keeps
  // one aligned column per cell across all rows.
  return `
  <div class="dashboard-history-row${clickable ? ' is-clickable' : ''}" data-doc-id="${escapeHtml(entry.key)}"${clickable ? ' tabindex="0"' : ''}>
    <span class="dashboard-card-name" title="${escapeHtml(entry.name)}">${escapeHtml(displayName(entry.name))}${warn}</span>
    <span class="dashboard-history-cell">${pillHtml(pill)}</span>
    <span class="dashboard-history-cell">${countsHtml(entry)}</span>
    <span class="dashboard-history-cell dashboard-card-date">${entry.updatedAt ? escapeHtml(formatDate(entry.updatedAt)) : ''}</span>
    <span class="dashboard-history-cell dashboard-history-actions">${actionsHtml(entry, state)}</span>
    <div class="dashboard-reports-panel" hidden></div>
  </div>`;
}

// Items for the ⋯ menu. Everything that is not the primary action lives here.
function menuItems(entry, { isActive, projects }) {
  const state = cardState(entry);
  const busy = isBusy(state);
  const hasReports = (entry.reportCount || 0) > 0 && !!entry.documentId;
  const items = [];

  if (canOpenEntry(entry, state) && !busy) items.push({ action: 'open', label: 'Open document' });
  if (hasReports) items.push({ action: 'reports', label: `Reports and sharing (${entry.reportCount})` });
  if (hasReports && !busy) items.push({ action: 'email', label: 'Email report' });

  if (entry.documentId && !busy) {
    const moves = [];
    if (isActive && entry.inRegistry) moves.push({ action: 'demote', label: 'Move to past documents' });
    if (!isActive) moves.push({ action: 'promote', label: 'Move to active documents' });
    for (const name of projects) {
      if (name !== entry.project) moves.push({ action: 'project', value: name, label: `Move to “${name}”` });
    }
    moves.push({ action: 'project-new', label: 'Move to new project…' });
    if (entry.project) moves.push({ action: 'project', value: '', label: `Remove from “${entry.project}”` });
    if (items.length) items.push({ separator: true });
    items.push(...moves);
  }

  if (items.length) items.push({ separator: true });
  items.push({ action: 'delete', label: 'Delete', danger: true });
  return items;
}

export function initializeDashboard({
  root,
  activeContainer,
  historyContainer,
  historyHeader,
  searchInput,
  dropzone,
  fileInput,
  emailToggle,
  verifyAllBtn,
  refreshBtn,
  store,
  onOpenDocument,
  setStatus = () => {},
  signal,
}) {
  let loaded = false;
  let searchQuery = '';

  // Reports panels are drilled down manually, but the dashboard re-renders
  // often (job polling, cache-summary hydration). To avoid a fetch-and-flicker
  // on every re-render, we track which panels are open and cache their rendered
  // HTML: a re-render restores the cached content instantly, and we only refetch
  // on an explicit open, after share/unshare, or when the report count changed.
  const expandedReports = new Set();      // entryKeys whose panel is open
  const reportsCache = new Map();         // entryKey -> { html, count }
  const reportsLoading = new Set();       // entryKeys with an in-flight fetch

  // Collapsed project groups persist across sessions (names, not keys).
  const COLLAPSED_PROJECTS_KEY = 'fc_dashboard_collapsed_projects';
  const AUTO_EMAIL_REPORTS_KEY = 'fc_dashboard_auto_email_reports';
  let collapsedProjects;
  try {
    collapsedProjects = new Set(JSON.parse(localStorage.getItem(COLLAPSED_PROJECTS_KEY) || '[]'));
  } catch {
    collapsedProjects = new Set();
  }
  const persistCollapsed = () => {
    try { localStorage.setItem(COLLAPSED_PROJECTS_KEY, JSON.stringify([...collapsedProjects])); } catch { /* best-effort */ }
  };
  try {
    if (emailToggle) emailToggle.checked = localStorage.getItem(AUTO_EMAIL_REPORTS_KEY) === '1';
  } catch { /* best-effort */ }
  emailToggle?.addEventListener('change', () => {
    try { localStorage.setItem(AUTO_EMAIL_REPORTS_KEY, emailToggle.checked ? '1' : '0'); } catch { /* best-effort */ }
    setStatus(emailToggle.checked
      ? 'Reports will be emailed automatically after verification.'
      : 'Automatic report emails are off. You can still email any completed report.');
  }, { signal });

  // Make an expanded doc's panel visible and paint the best content we have
  // (cache, or a loading hint). Called on open and on every re-render, so the
  // panel is never left collapsed mid-fetch. Panel elements are looked up fresh
  // each time because renders replace them.
  const renderReportsPanel = (entryKey) => {
    if (!expandedReports.has(entryKey)) return;
    const holder = root?.querySelector(`[data-doc-id="${CSS.escape(entryKey)}"]`);
    const panel = holder?.querySelector('.dashboard-reports-panel');
    if (!panel) return;
    panel.hidden = false;
    holder.classList.add('has-open-reports');
    const cached = reportsCache.get(entryKey);
    panel.innerHTML = cached ? cached.html : '<span class="small muted">Loading reports…</span>';
  };

  // Fetch reports into the cache and repaint. De-duped; a no-op fetch is skipped
  // when the cache already matches the current report count (unless forced).
  const refreshReports = async (entryKey, { force = false } = {}) => {
    const count = store.getEntry(entryKey)?.reportCount || 0;
    const cached = reportsCache.get(entryKey);
    if (reportsLoading.has(entryKey)) return;
    if (!force && cached && cached.count === count) { renderReportsPanel(entryKey); return; }
    reportsLoading.add(entryKey);
    renderReportsPanel(entryKey); // show cache-or-loading immediately
    try {
      const reports = await store.fetchReports(entryKey);
      const newestTypes = new Set();
      const html = reports.length
        ? reports.map((report) => {
            const type = String(report.report_type || 'html');
            const canManageShare = !newestTypes.has(type);
            newestTypes.add(type);
            return reportRowHtml(report, { canManageShare });
          }).join('')
        : '<span class="small muted">No reports found.</span>';
      reportsCache.set(entryKey, { html, count });
    } catch (err) {
      // count:-1 never matches a real count, so the next render retries.
      reportsCache.set(entryKey, {
        html: `<span class="small muted">Could not load reports: ${escapeHtml(err.message || err)}</span>`,
        count: -1,
      });
    } finally {
      reportsLoading.delete(entryKey);
      renderReportsPanel(entryKey);
    }
  };

  const closeReportsPanels = () => {
    for (const openKey of expandedReports) {
      const openHolder = root?.querySelector(`[data-doc-id="${CSS.escape(openKey)}"]`);
      const openPanel = openHolder?.querySelector('.dashboard-reports-panel');
      if (openPanel) openPanel.hidden = true;
      openHolder?.classList.remove('has-open-reports');
    }
    expandedReports.clear();
  };

  // "Show more" widens the store's recency window; filed/pinned docs are
  // always complete, so the button only extends the unfiled list.
  const showMoreHtml = () => (store.hasMoreRecent && !searchQuery
    ? '<div class="dashboard-show-more"><button class="btn-text btn-small dashboard-show-more-btn" type="button">Show older documents…</button></div>'
    : '');

  const matchesSearch = (entry) => !searchQuery
    || String(entry.name || '').toLowerCase().includes(searchQuery)
    || String(entry.project || '').toLowerCase().includes(searchQuery);

  // History rendered as: unfiled rows first (most recent work), then one
  // collapsible group per project, then "show older" at the very bottom.
  // While searching, every group is expanded so matches are never hidden.
  const historyHtml = (history) => {
    const visible = history.filter(matchesSearch);
    if (!visible.length) {
      if (searchQuery) return '<div class="dashboard-empty">No documents match your search.</div>';
      return showMoreHtml();
    }
    const groups = new Map();
    for (const e of visible) {
      if (!e.project) continue;
      if (!groups.has(e.project)) groups.set(e.project, []);
      groups.get(e.project).push(e);
    }
    let html = visible.filter((e) => !e.project).map(historyRowHtml).join('');
    for (const name of Array.from(groups.keys()).sort((a, b) => a.localeCompare(b))) {
      const rows = groups.get(name);
      const isCollapsed = collapsedProjects.has(name) && !searchQuery;
      html += `
      <div class="dashboard-project-header" data-project="${escapeHtml(name)}" role="button" tabindex="0"
        aria-expanded="${!isCollapsed}" title="${isCollapsed ? 'Expand' : 'Collapse'} this project">
        <span class="dashboard-project-caret">${isCollapsed ? '▸' : '▾'}</span>
        <span class="dashboard-project-name">${escapeHtml(name)}</span>
        <span class="dashboard-project-count">${rows.length}</span>
      </div>`;
      if (!isCollapsed) html += rows.map(historyRowHtml).join('');
    }
    return html + showMoreHtml();
  };

  const isActiveEntry = (e) => !!(e.session || e.job || e.pinned);

  const renderNow = (entries) => {
    if (!activeContainer || !historyContainer) return;

    // Active zone: this session's uploads, running jobs, and pinned documents
    // (the persisted promote/demote state).
    const active = entries
      .filter(isActiveEntry)
      .sort((a, b) => (a.seq || 0) - (b.seq || 0));
    const history = entries
      .filter((e) => !isActiveEntry(e) && e.inRegistry)
      .sort((a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')));

    // First-run: no documents at all turns the upload strip into a welcome panel.
    root?.classList.toggle('is-empty', loaded && !active.length && !history.length && !store.hasMoreRecent);

    activeContainer.innerHTML = active.map(cardHtml).join('');
    activeContainer.hidden = !active.length;

    if (historyHeader) historyHeader.hidden = loaded && !history.length && !store.hasMoreRecent;
    if (searchInput) searchInput.hidden = history.length <= 10 && !searchQuery;

    if (!loaded) {
      historyContainer.innerHTML = '<div class="dashboard-loading"><div class="loading-spinner-small"></div><span>Loading your documents…</span></div>';
    } else {
      historyContainer.innerHTML = historyHtml(history);
    }

    // Bulk verify only earns a button when there is more than one thing to run.
    if (verifyAllBtn) {
      const pending = store.pendingEntries().length;
      verifyAllBtn.hidden = pending < 2;
      verifyAllBtn.textContent = `Verify all new (${pending})`;
    }

    // Re-open (restore) any expanded panels immediately from cache (no flicker,
    // no collapse), then silently refetch only if the report count changed.
    for (const key of expandedReports) {
      const holder = root?.querySelector(`[data-doc-id="${CSS.escape(key)}"]`);
      if (!holder) { expandedReports.delete(key); continue; }
      renderReportsPanel(key);
      refreshReports(key);
    }
  };

  const render = (entries) => renderNow(entries);

  store.subscribe(render);
  render(store.getEntries());

  const wantEmail = () => !!emailToggle?.checked;

  // --- ⋯ menu: one popover on <body>, so dashboard re-renders never close it ---
  const menu = document.createElement('div');
  menu.className = 'dashboard-menu';
  menu.setAttribute('role', 'menu');
  menu.hidden = true;
  document.body.appendChild(menu);
  signal?.addEventListener('abort', () => menu.remove());
  let menuKey = null;

  const closeMenu = () => {
    menu.hidden = true;
    menuKey = null;
  };

  const openMenu = (entryKey, anchor) => {
    const entry = store.getEntry(entryKey);
    if (!entry) return;
    if (menuKey === entryKey && !menu.hidden) { closeMenu(); return; }
    const items = menuItems(entry, { isActive: isActiveEntry(entry), projects: store.listProjects() });
    menu.innerHTML = items.map((item) => (item.separator
      ? '<div class="dashboard-menu-separator" role="separator"></div>'
      : `<button type="button" role="menuitem" class="dashboard-menu-item${item.danger ? ' is-danger' : ''}"
          data-action="${item.action}"${item.value != null ? ` data-value="${escapeHtml(item.value)}"` : ''}>${escapeHtml(item.label)}</button>`))
      .join('');
    menuKey = entryKey;
    menu.hidden = false;

    // Below-right of the button, flipped up/left when it would leave the viewport.
    const rect = anchor.getBoundingClientRect();
    const { offsetWidth: w, offsetHeight: h } = menu;
    const left = Math.max(8, Math.min(rect.right - w, window.innerWidth - w - 8));
    const below = rect.bottom + 4;
    const top = below + h > window.innerHeight - 8 ? Math.max(8, rect.top - h - 4) : below;
    menu.style.left = `${left + window.scrollX}px`;
    menu.style.top = `${top + window.scrollY}px`;
    menu.querySelector('.dashboard-menu-item')?.focus();
  };

  document.addEventListener('click', (event) => {
    if (menu.hidden) return;
    if (menu.contains(event.target) || event.target.closest?.('.dashboard-menu-btn')) return;
    closeMenu();
  }, { signal });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !menu.hidden) closeMenu();
  }, { signal });
  window.addEventListener('resize', closeMenu, { signal });

  menu.addEventListener('click', (event) => {
    const item = event.target.closest('.dashboard-menu-item');
    if (!item || !menuKey) return;
    const entryKey = menuKey;
    closeMenu();
    runAction(item.dataset.action, entryKey, { value: item.dataset.value });
  }, { signal });

  // --- Dropzone: adding files never starts processing (user decides) ---
  const acceptFiles = async (fileList) => {
    const added = await store.addFiles(fileList);
    if (!added) return;
    setStatus(added === 1
      ? 'Document added. Click Verify to check its citations.'
      : `Added ${added} documents. Click Verify on each, or "Verify all new" to start them together.`);
  };

  dropzone?.addEventListener('click', () => fileInput?.click(), { signal });
  dropzone?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInput?.click(); }
  }, { signal });
  dropzone?.addEventListener('dragover', (e) => { e.preventDefault(); dropzone.classList.add('dragover'); }, { signal });
  dropzone?.addEventListener('dragleave', () => dropzone.classList.remove('dragover'), { signal });
  dropzone?.addEventListener('drop', (e) => {
    e.preventDefault();
    dropzone.classList.remove('dragover');
    acceptFiles(e.dataTransfer?.files).catch((err) => setStatus(`Could not add documents: ${err.message || err}`));
  }, { signal });
  fileInput?.addEventListener('change', () => {
    acceptFiles(fileInput.files).catch((err) => setStatus(`Could not add documents: ${err.message || err}`));
    fileInput.value = '';
  }, { signal });

  // --- Header actions ---
  verifyAllBtn?.addEventListener('click', () => {
    const pending = store.pendingEntries().length;
    if (!pending) {
      setStatus('Nothing new to verify. Drop PDFs above first.');
      return;
    }
    if (!window.confirm(`Verify ${plural(pending, 'document')}? Each verification uses credits from your balance.`)) return;
    const submitted = store.verifyAllPending({ email: wantEmail() });
    setStatus(`Started verifying ${plural(submitted, 'document')}.`);
  }, { signal });

  refreshBtn?.addEventListener('click', () => {
    store.refresh().catch((err) => setStatus(`Refresh failed: ${err.message || err}`));
  }, { signal });

  searchInput?.addEventListener('input', () => {
    searchQuery = searchInput.value.trim().toLowerCase();
    render(store.getEntries());
  }, { signal });

  // --- Project group headers: collapse/expand (click or Enter/Space) ---
  const toggleProjectGroup = (header) => {
    const name = header.dataset.project;
    if (!name) return;
    if (collapsedProjects.has(name)) collapsedProjects.delete(name);
    else collapsedProjects.add(name);
    persistCollapsed();
    render(store.getEntries());
  };

  const openReport = async (entryKey, reportId, button) => {
    const popup = window.open('about:blank', '_blank');
    if (popup) {
      popup.opener = null;
      popup.document.title = 'Opening report…';
      popup.document.body.textContent = 'Opening report…';
    }
    if (button) button.disabled = true;
    try {
      let id = reportId;
      if (!id) {
        // "View report": the newest HTML report (reports arrive newest first).
        const reports = await store.fetchReports(entryKey);
        const newest = reports.find((r) => String(r.report_type || 'html') === 'html') || reports[0];
        if (!newest) throw new Error('No report found.');
        id = newest.report_id;
      }
      const blob = await store.fetchReportContent(entryKey, id);
      const objectUrl = URL.createObjectURL(blob);
      if (popup) {
        popup.location.replace(objectUrl);
      } else {
        const fallback = document.createElement('a');
        fallback.href = objectUrl;
        fallback.target = '_blank';
        fallback.rel = 'noopener';
        fallback.click();
      }
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch (err) {
      popup?.close();
      setStatus(`Could not open report: ${err.message || err}`);
    } finally {
      if (button) button.disabled = false;
    }
  };

  const moveToProject = async (entryKey, value) => {
    const entry = store.getEntry(entryKey);
    if (!entry) return;
    try {
      await store.updateOrganization(entryKey, { project: value || null });
      setStatus(value ? `Moved "${displayName(entry.name)}" to "${value}".` : `Removed "${displayName(entry.name)}" from its project.`);
    } catch (err) {
      setStatus(`Could not update project: ${err.message || err}`);
    }
  };

  // Every per-document action, whether it came from a button or the ⋯ menu.
  const runAction = async (action, entryKey, { button = null, anchor = null, value } = {}) => {
    const entry = store.getEntry(entryKey);
    if (!entry) return;

    switch (action) {
      case 'menu':
        openMenu(entryKey, anchor);
        return;

      case 'open':
        onOpenDocument?.(entry.documentId, entry);
        return;

      case 'view-report':
        await openReport(entryKey, null, button);
        return;

      case 'verify':
        store.submit(entryKey, { email: wantEmail() });
        return;

      case 'stop': {
        if (button) button.disabled = true;
        try {
          const result = await store.cancelJob(entryKey);
          setStatus(result?.status === 'cancelled' ? 'Verification cancelled.' : 'Stopping the verification run…');
        } catch (err) {
          setStatus(`Could not stop the job: ${err.message || err}`);
          if (button) button.disabled = false;
        }
        return;
      }

      case 'email':
        try {
          const result = await store.emailExistingReports(entryKey);
          setStatus(`Sent ${plural(result.report_count, 'report link')} to ${result.sent_to}.`);
        } catch (err) {
          setStatus(`Email failed: ${err.message || err}`);
        }
        return;

      case 'reports': {
        const wasOpen = expandedReports.has(entryKey);
        closeReportsPanels();
        if (wasOpen) return;
        expandedReports.add(entryKey);
        renderReportsPanel(entryKey);
        refreshReports(entryKey);
        return;
      }

      case 'promote':
        try {
          await store.updateOrganization(entryKey, { pinned: true });
          setStatus(`"${displayName(entry.name)}" moved to your active documents.`);
        } catch (err) {
          setStatus(`Could not move document: ${err.message || err}`);
        }
        return;

      case 'demote':
        try {
          await store.demote(entryKey);
          setStatus(`"${displayName(entry.name)}" moved to past documents.`);
        } catch (err) {
          setStatus(`Could not move document: ${err.message || err}`);
        }
        return;

      case 'project':
        await moveToProject(entryKey, value || '');
        return;

      case 'project-new': {
        const name = window.prompt('New project name:', '');
        if (name === null || !name.trim()) return;
        await moveToProject(entryKey, name.trim());
        return;
      }

      case 'delete':
        if (!window.confirm(`Delete "${displayName(entry.name)}" and all its data? This cannot be undone.`)) return;
        try {
          await store.deleteDocument(entryKey);
          setStatus('Document deleted.');
        } catch (err) {
          setStatus(`Delete failed: ${err.message || err}`);
        }
        return;

      default:
    }
  };

  root?.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const header = event.target.closest('.dashboard-project-header');
    if (header) { event.preventDefault(); toggleProjectGroup(header); return; }
    // Keyboard equivalent of clicking a row/card: only when the row itself has focus.
    const holder = event.target.closest('.is-clickable[data-doc-id]');
    if (holder && event.target === holder) {
      event.preventDefault();
      runAction('open', holder.dataset.docId);
    }
  }, { signal });

  // --- Card/row actions (delegated on the dashboard root) ---
  root?.addEventListener('click', async (event) => {
    const projectHeader = event.target.closest('.dashboard-project-header');
    if (projectHeader) { toggleProjectGroup(projectHeader); return; }

    if (event.target.closest('.dashboard-show-more-btn')) {
      const button = event.target.closest('.dashboard-show-more-btn');
      button.disabled = true;
      button.textContent = 'Loading…';
      try {
        await store.loadMoreRecent();
      } catch (err) {
        setStatus(`Could not load more documents: ${err.message || err}`);
        button.disabled = false;
        button.textContent = 'Show older documents…';
      }
      return;
    }

    const holder = event.target.closest('[data-doc-id]');
    if (!holder) return;
    const entryKey = holder.dataset.docId;
    const entry = store.getEntry(entryKey);
    if (!entry) return;

    const actionButton = event.target.closest('[data-action]');
    if (actionButton) {
      runAction(actionButton.dataset.action, entryKey, { button: actionButton, anchor: actionButton });
      return;
    }

    if (event.target.closest('.dashboard-report-link')) {
      const button = event.target.closest('.dashboard-report-link');
      await openReport(entryKey, button.dataset.reportId, button);
      return;
    }

    if (event.target.closest('.dashboard-share')) {
      const button = event.target.closest('.dashboard-share');
      const reportId = button.dataset.reportId;
      // null = cancelled; '' = share without a password.
      const password = window.prompt(
        'Create a share link.\n\nOptional: set a password recipients must enter (leave blank for none):',
        '',
      );
      if (password === null) return;
      const deferredCopy = beginDeferredClipboardCopy();
      button.disabled = true;
      try {
        const result = await store.shareReport(entryKey, reportId, { password: password.trim() });
        const deferredCopied = deferredCopy ? await deferredCopy.complete(result.share_url) : false;
        const copied = deferredCopied || await copyToClipboard(result.share_url);
        setStatus(copied
          ? `Share link copied to clipboard${result.has_password ? ' (password protected)' : ''}.`
          : `Report shared${result.has_password ? ' (password protected)' : ''}. Use "Copy link" to grab the URL.`);
        await refreshReports(entryKey, { force: true });
      } catch (err) {
        deferredCopy?.cancel();
        setStatus(`Could not share report: ${err.message || err}`);
        button.disabled = false;
      }
      return;
    }

    if (event.target.closest('.dashboard-share-copy')) {
      const button = event.target.closest('.dashboard-share-copy');
      const copied = await copyToClipboard(button.dataset.shareUrl);
      setStatus(copied ? 'Share link copied to clipboard.' : 'Could not copy — the link is shown in the report panel.');
      return;
    }

    if (event.target.closest('.dashboard-share-revoke')) {
      const button = event.target.closest('.dashboard-share-revoke');
      const reportId = button.dataset.reportId;
      if (!window.confirm('Stop sharing this report? The existing link will stop working immediately.')) return;
      button.disabled = true;
      try {
        await store.revokeReportShare(entryKey, reportId);
        setStatus('Sharing disabled — the link no longer works.');
        await refreshReports(entryKey, { force: true });
      } catch (err) {
        setStatus(`Could not unshare report: ${err.message || err}`);
        button.disabled = false;
      }
      return;
    }

    // Anywhere else on a clickable row/card opens the document — except inside
    // the reports panel or the busy overlay.
    if (event.target.closest('.dashboard-reports-panel, .dashboard-card-overlay')) return;
    if (holder.classList.contains('is-clickable')) runAction('open', entryKey);
  }, { signal });

  // Initial load + rehydration of in-flight jobs (plan 2A.7).
  store.refresh()
    .catch((err) => {
      console.error('[Dashboard] Initial load failed:', err);
      setStatus(`Could not load your documents: ${err.message || err}`);
    })
    .finally(() => {
      loaded = true;
      render(store.getEntries());
    });

  return { render };
}
